import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import BarangTerkini from '../components/Admin/Summary/BarangTerkini';
import SmPembelian from '../components/Admin/Summary/SmPembelian';
import SmPenjualan from '../components/Admin/Summary/SmPenjualan';
import SmTransaksiTertunda from '../components/Admin/Summary/SmTransaksiTertunda';
import { getAuthorizationHeader, http } from '../config/http';
import Layouts from '../layouts/Layouts';
import { DinamicTitle } from '../utils/DinamicTitle';
import moment from 'moment';

/**
 * Helper: build query string dari object params.
 * Mengabaikan value kosong/whitespace-only.
 * @param {Object} params - { cabang: '1', tanggal: '22-03-2026', karat: '24' }
 * @returns {string} - '?cabang=1&tanggal=22-03-2026&karat=24'
 */
function buildQueryString(params) {
  const entries = Object.entries(params).filter(
    ([, v]) => v !== undefined && v !== null && String(v).trim() !== ''
  );
  if (entries.length === 0) return '';
  return '?' + entries.map(([k, v]) => `${k}=${encodeURIComponent(v)}`).join('&');
}

/**
 * Cache TTL in milliseconds (5 minutes).
 * Data yang di-fetch dalam window ini tidak akan di-fetch ulang.
 */
const CACHE_TTL = 5 * 60 * 1000;

function Summary() {
  DinamicTitle('Ringkasan Transaksi || Komaladewi');
  const navigate = useNavigate();

  // ─── Cabang Filter (shared across all sections) ───
  const [cabangFilterSm, setCabangFilterSm] = useState([]);

  // ─── Penjualan State ───
  const [summaryPenjualan, setSummaryPenjualan] = useState([]);
  const [loadingDataPenjualan, setLoadingDataPenjualan] = useState(false);
  const [idCabangFilterPenjualan, setIdCabangFilterPenjualan] = useState('');
  const [karatFilterPenjualan, setKaratFilterPenjualan] = useState('');
  const [dateFilterPenjualan, setDateFilterPenjualan] = useState(moment().format('DD-MM-YYYY'));
  const [totalsPenjualan, setTotalsPenjualan] = useState({
    harga: '', berat: '', biji: '',
  });

  // ─── Pembelian State ───
  const [summaryPembelian, setSummaryPembelian] = useState([]);
  const [loadingDataPembelian, setLoadingDataPembelian] = useState(false);
  const [idCabangFilterPembelian, setIdCabangFilterPembelian] = useState('');
  const [karatFilterPembelian, setKaratFilterPembelian] = useState('');
  const [dateFilterPembelian, setDateFilterPembelian] = useState(moment().format('DD-MM-YYYY'));
  const [totalsPembelian, setTotalsPembelian] = useState({
    harga: '', berat: '', biji: '',
  });

  // ─── Kondisi Item Pembelian ───
  const [kondisiItemPembelian, setKondisiPembelianItem] = useState([]);

  // ─── Barang Terkini State ───
  const [barangTerkini, setBarangTerkini] = useState([]);
  const [loadingBarangTerkini, setLoadingBarangTerkini] = useState(false);
  const [idCabangFilterBT, setFilterCabangBT] = useState('');
  const [karatFilterBT, setKaratFilterBT] = useState('');
  const [dateFilterBT, setDateFilterBT] = useState(moment().format('DD-MM-YYYY'));
  const [totalsBarangTerkini, setTotalsBarangTerkini] = useState({
    harga: '', berat: '', biji: '',
  });

  // ─── Transaksi Tertunda State ───
  const [summaryTransaksiTertunda, setSummaryTransaksiTertunda] = useState([]);
  const [loadingDataTransaksiTertunda, setLoadingDataTransaksiTertunda] = useState(false);
  const [idCabangFilterTransaksiTertunda, setIdCabangFilterTransaksiTertunda] = useState('');
  const [karatFilterTransaksiTertunda, setKaratTransaksiTertunda] = useState('');
  const [dateFilterTransaksiTertunda, setDateFilterTransaksiTertunda] = useState(moment().format('DD-MM-YYYY'));
  const [totalsTransaksiTertunda, setTotalsTransaksiTertunda] = useState({
    harga: '', berat: '', biji: '',
  });

  // ─── Refresh Counters (to force re-fetch) ───
  const [refreshPenjualan, setRefreshPenjualan] = useState(0);
  const [refreshPembelian, setRefreshPembelian] = useState(0);
  const [refreshBT, setRefreshBT] = useState(0);
  const [refreshTransaksiTertunda, setRefreshTransaksiTertunda] = useState(0);

  // ─── Cache Ref ───
  const cacheRef = useRef({});

  /**
   * Generic fetch with AbortController, caching, and error handling.
   * @param {string} url - API endpoint (tanpa query string)
   * @param {Object} params - filter parameters
   * @param {Object} options - { setLoading, onSuccess, signal }
   */
  const fetchWithCache = useCallback(async (url, params, { setLoading, onSuccess, signal }) => {
    const qs = buildQueryString(params);
    const fullUrl = `${url}${qs}`;
    const cacheKey = fullUrl;

    // Check cache
    const cached = cacheRef.current[cacheKey];
    if (cached && Date.now() - cached.timestamp < CACHE_TTL) {
      onSuccess(cached.data);
      return;
    }

    setLoading(true);
    try {
      const res = await http.get(fullUrl, {
        headers: { Authorization: getAuthorizationHeader() },
        signal,
      });
      // Store in cache
      cacheRef.current[cacheKey] = { data: res.data, timestamp: Date.now() };
      onSuccess(res.data);
    } catch (err) {
      if (err.name === 'CanceledError' || err.name === 'AbortError') return;
      if (err.response === undefined) return;
      if (err.response?.status === 401) navigate('/login');
    } finally {
      if (!signal?.aborted) setLoading(false);
    }
  }, [navigate]);

  // ─── Fetch Cabang (hanya sekali) ───
  useEffect(() => {
    const controller = new AbortController();
    fetchWithCache('/cabang', {}, {
      setLoading: () => { },
      onSuccess: (data) => {
        const list = data.data || [];
        setCabangFilterSm(list);
        
        // Auto-select first Etalase (non-brankas) for specific widgets
        const firstEtalase = list.find(c => !c.nama_cabang?.toLowerCase().includes('brankas'));
        if (firstEtalase) {
          setIdCabangFilterPenjualan(firstEtalase.id);
          setIdCabangFilterPembelian(firstEtalase.id);
          setIdCabangFilterTransaksiTertunda(firstEtalase.id);
        }
      },
      signal: controller.signal,
    });
    return () => controller.abort();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ─── Fetch Penjualan ───
  useEffect(() => {
    const controller = new AbortController();
    fetchWithCache('/summary/penjualan', {
      cabang: idCabangFilterPenjualan,
      tanggal: dateFilterPenjualan,
      karat: karatFilterPenjualan,
    }, {
      setLoading: setLoadingDataPenjualan,
      onSuccess: (data) => {
        setTotalsPenjualan({
          berat: data.total_berat_perhari,
          biji: data.total_item_perhari,
          harga: data.total_harga_perhari,
        });
        setSummaryPenjualan(data.data);
      },
      signal: controller.signal,
    });
    return () => controller.abort();
  }, [idCabangFilterPenjualan, dateFilterPenjualan, karatFilterPenjualan, refreshPenjualan]); // eslint-disable-line react-hooks/exhaustive-deps

  // ─── Fetch Pembelian ───
  useEffect(() => {
    const controller = new AbortController();
    fetchWithCache('/summary/pembelian', {
      cabang: idCabangFilterPembelian,
      tanggal: dateFilterPembelian,
      karat: karatFilterPembelian,
    }, {
      setLoading: setLoadingDataPembelian,
      onSuccess: (data) => {
        setTotalsPembelian({
          biji: data.data.total_item_perhari,
          harga: data.data.total_harga_perhari,
          berat: data.data.total_berat_perhari,
        });
        setKondisiPembelianItem(data.data.summary_kondisi_item_pembelian);
        setSummaryPembelian(data.data.summary_item_pembelian);
      },
      signal: controller.signal,
    });
    return () => controller.abort();
  }, [idCabangFilterPembelian, dateFilterPembelian, karatFilterPembelian, refreshPembelian]); // eslint-disable-line react-hooks/exhaustive-deps

  // ─── Fetch Barang Terkini ───
  useEffect(() => {
    const controller = new AbortController();
    fetchWithCache('/summary/item_terkini', {
      cabang: idCabangFilterBT,
      karat: karatFilterBT,
    }, {
      setLoading: setLoadingBarangTerkini,
      onSuccess: (data) => {
        setBarangTerkini(data.data);
        setTotalsBarangTerkini({
          berat: data.total_berat,
          biji: data.total_item,
          harga: data.total_modal,
        });
      },
      signal: controller.signal,
    });
    return () => controller.abort();
  }, [idCabangFilterBT, dateFilterBT, karatFilterBT, refreshBT]); // eslint-disable-line react-hooks/exhaustive-deps

  // ─── Fetch Transaksi Tertunda ───
  useEffect(() => {
    const controller = new AbortController();
    fetchWithCache('/transaksi_tertunda/summary', {
      cabang: idCabangFilterTransaksiTertunda,
      tanggal: dateFilterTransaksiTertunda,
      karat: karatFilterTransaksiTertunda,
    }, {
      setLoading: setLoadingDataTransaksiTertunda,
      onSuccess: (data) => {
        setTotalsTransaksiTertunda({
          berat: data.total_berat_perhari,
          biji: data.total_item_perhari,
          harga: data.total_harga_perhari,
        });
        setSummaryTransaksiTertunda(data.data);
      },
      signal: controller.signal,
    });
    return () => controller.abort();
  }, [idCabangFilterTransaksiTertunda, dateFilterTransaksiTertunda, karatFilterTransaksiTertunda, refreshTransaksiTertunda]); // eslint-disable-line react-hooks/exhaustive-deps

  // ─── Reload Functions ───
  function reloadPenjualan() {
    Object.keys(cacheRef.current).forEach((key) => {
      if (key.startsWith('/summary/penjualan')) delete cacheRef.current[key];
    });
    const firstEtalase = cabangFilterSm.find(c => !c.nama_cabang?.toLowerCase().includes('brankas'));
    setIdCabangFilterPenjualan(firstEtalase ? firstEtalase.id : '');
    setKaratFilterPenjualan('');
    setDateFilterPenjualan(moment().format('DD-MM-YYYY'));
    setRefreshPenjualan(prev => prev + 1);
  }

  function reloadPembelian() {
    Object.keys(cacheRef.current).forEach((key) => {
      if (key.startsWith('/summary/pembelian')) delete cacheRef.current[key];
    });
    const firstEtalase = cabangFilterSm.find(c => !c.nama_cabang?.toLowerCase().includes('brankas'));
    setIdCabangFilterPembelian(firstEtalase ? firstEtalase.id : '');
    setKaratFilterPembelian('');
    setDateFilterPembelian(moment().format('DD-MM-YYYY'));
    setRefreshPembelian(prev => prev + 1);
  }

  function reloadBarangTerkini() {
    Object.keys(cacheRef.current).forEach((key) => {
      if (key.startsWith('/summary/item_terkini')) delete cacheRef.current[key];
    });
    setFilterCabangBT(''); // Barang Terkini tetap bisa '' (Semua)
    setKaratFilterBT('');
    setDateFilterBT(moment().format('DD-MM-YYYY'));
    setRefreshBT(prev => prev + 1);
  }

  function reloadTransaksiTertunda() {
    Object.keys(cacheRef.current).forEach((key) => {
      if (key.startsWith('/transaksi_tertunda/summary')) delete cacheRef.current[key];
    });
    const firstEtalase = cabangFilterSm.find(c => !c.nama_cabang?.toLowerCase().includes('brankas'));
    setIdCabangFilterTransaksiTertunda(firstEtalase ? firstEtalase.id : '');
    setKaratTransaksiTertunda('');
    setDateFilterTransaksiTertunda(moment().format('DD-MM-YYYY'));
    setRefreshTransaksiTertunda(prev => prev + 1);
  }

  return (
    <Layouts
    // title={'LAPORAN TRANSAKSI'}
    >
      <div className="grid lg:grid-cols-2 gap-2 m-2">
        <div className="w-full bg-white dark:bg-slate-800 shadow-md rounded border border-transparent dark:border-slate-700/50">
          <SmPenjualan
            setIdCabangFilterPenjualan={setIdCabangFilterPenjualan}
            setDateFilterPenjualan={setDateFilterPenjualan}
            cabangFilterSm={cabangFilterSm}
            loadingDataPenjualan={loadingDataPenjualan}
            totalBeratPenjualan={totalsPenjualan.berat}
            totalHargaPenjualan={totalsPenjualan.harga}
            totalBijiPenjualan={totalsPenjualan.biji}
            summaryPenjualan={summaryPenjualan}
            reloadPenjualan={reloadPenjualan}
            setKaratFilterPenjualan={setKaratFilterPenjualan}
            idCabangFilterPenjualan={idCabangFilterPenjualan}
            karatFilterPenjualan={karatFilterPenjualan}
            dateFilterPenjualan={dateFilterPenjualan}
          />
        </div>

        <div className="w-full bg-white dark:bg-slate-800 shadow-md rounded border border-transparent dark:border-slate-700/50">
          <SmPembelian
            cabangFilterSm={cabangFilterSm}
            totalHargaPembelian={totalsPembelian.harga}
            totalBeratPembelian={totalsPembelian.berat}
            totalBijiPembelian={totalsPembelian.biji}
            summaryPembelian={summaryPembelian}
            loadingDataPembelian={loadingDataPembelian}
            setIdCabangFilterPembelian={setIdCabangFilterPembelian}
            setDateFilterPembelian={setDateFilterPembelian}
            reloadPembelian={reloadPembelian}
            setKaratFilterPembelian={setKaratFilterPembelian}
            idCabangFilterPembelian={idCabangFilterPembelian}
            karatFilterPembelian={karatFilterPembelian}
            dateFilterPembelian={dateFilterPembelian}
          />
        </div>

        <div className="w-full bg-white dark:bg-slate-800 shadow-md rounded border border-transparent dark:border-slate-700/50">
          <BarangTerkini
            setDateFilterBT={setDateFilterBT}
            setFilterCabangBT={setFilterCabangBT}
            cabangFilterSm={cabangFilterSm}
            reloadBarangTerkini={reloadBarangTerkini}
            totalBijiBarangTerkini={totalsBarangTerkini.biji}
            totalHargaBarangTerkini={totalsBarangTerkini.harga}
            totalBeratBarangTerkini={totalsBarangTerkini.berat}
            loadingBarangTerkini={loadingBarangTerkini}
            barangTerkini={barangTerkini}
            setKaratFilterBT={setKaratFilterBT}
            idCabangFilterBT={idCabangFilterBT}
            karatFilterBT={karatFilterBT}
          />
        </div>

        <div className="w-full bg-white dark:bg-slate-800 shadow-md rounded border border-transparent dark:border-slate-700/50">
          <SmTransaksiTertunda
            setIdCabangFilterPenjualan={setIdCabangFilterTransaksiTertunda}
            setDateFilterPenjualan={setDateFilterTransaksiTertunda}
            cabangFilterSm={cabangFilterSm}
            loadingDataPenjualan={loadingDataTransaksiTertunda}
            totalBeratPenjualan={totalsTransaksiTertunda.berat}
            totalHargaPenjualan={totalsTransaksiTertunda.harga}
            totalBijiPenjualan={totalsTransaksiTertunda.biji}
            summaryPenjualan={summaryTransaksiTertunda}
            reloadPenjualan={reloadTransaksiTertunda}
            setKaratFilterPenjualan={setKaratTransaksiTertunda}
            idCabangFilterPenjualan={idCabangFilterTransaksiTertunda}
            karatFilterPenjualan={karatFilterTransaksiTertunda}
            dateFilterPenjualan={dateFilterTransaksiTertunda}
          />
        </div>
      </div>
    </Layouts>
  );
}

export default Summary;
